import { getAppApi } from '@/services/http.js'
import { memoize, round, startCase, uniqBy } from 'lodash'

export const RESIZE_MODE_SNAP = 'snap'
export const RESIZE_MODE_ASPECT = 'aspect'
export const RESIZE_MODE_FREE = 'free'
export const RESIZE_MODE_ASPECT_FREE = 'aspect-free'

function parsePositiveNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export function getItemDimensions(item) {
  if (!item || typeof item !== 'object') {
    return { width: null, height: null }
  }

  const candidates = [
    [item.max_width, item.max_height],
    [item.width, item.height],
    [item.pixel_width, item.pixel_height],
    [item.pixelWidth, item.pixelHeight],
    [item.w, item.h],
  ]

  for (const [width, height] of candidates) {
    const parsedWidth = Number(width)
    const parsedHeight = Number(height)
    if (Number.isFinite(parsedWidth) && Number.isFinite(parsedHeight) && parsedWidth > 0 && parsedHeight > 0) {
      return { width: parsedWidth, height: parsedHeight }
    }
  }

  return { width: null, height: null }
}

export function getItemAspectRatio(item) {
  const { width, height } = getItemDimensions(item)
  if (!width || !height) {
    return null
  }
  return width / height
}

function sizesApi(width, height) {
  return getAppApi().get(`/api/v1/availablesizes/?w=${width}&h=${height}`)
}

const sizesApiCached = memoize(sizesApi, (width, height) => {
  const ratio = width / height
  return isNaN(ratio) ? `${width},${height}` : ratio
})

export async function loadSizingChoices(item) {
  const { width, height } = getItemDimensions(item)
  if (!width || !height) {
    return { substrates: [], sizes: {} }
  }

  if (item?.substrates && item?.substrate_sizes) {
    return {
      substrates: item.substrates,
      sizes: item.substrate_sizes,
    }
  }

  const response = await sizesApiCached(width, height)
  return {
    substrates: item?.substrates || response.data?.substrates || [],
    sizes: item?.substrate_sizes || response.data?.sizes || {},
  }
}

// Studio suggestions until the size API supplies dimensions for these materials.
export function studioSizingChoices(item, sizes = {}) {
  if (item?.substrate_sizes) return sizes
  const ratio = getItemAspectRatio(item)
  const suggestions = ratio ? [48, 60, 72, 84, 96]
    .map(height => ({ width: Math.floor(height * ratio), height }))
    .filter(size => size.width > 0) : []
  return { ...sizes, mural: suggestions, 'window film': suggestions }
}

export function getSubstrateOptions(substrates, {
  labelKey = 'label',
  valueKey = 'value',
} = {}) {
  return (substrates || []).map((substrate) => {
    const isObject = substrate && typeof substrate === 'object'
    const label = isObject
      ? (substrate.name || substrate.label || substrate.text || substrate.value || substrate.id)
      : startCase(substrate)
    const value = isObject
      ? (substrate.id != null ? substrate.id : substrate.value)
      : substrate

    return {
      [labelKey]: label,
      [valueKey]: value,
    }
  })
}

function getSubstrateValue(substrate) {
  if (!substrate) return null
  if (typeof substrate === 'string') return substrate
  if (typeof substrate === 'number') return String(substrate)
  if (substrate.value != null) return substrate.value
  if (substrate.id != null) return substrate.id
  if (substrate.name != null) return substrate.name
  return null
}

function normalizeSubstrateKey(substrate) {
  const value = getSubstrateValue(substrate)
  return value == null ? '' : String(value).trim().toLowerCase()
}

function getSizesForSubstrate(substrate, sizes) {
  const directKey = getSubstrateValue(substrate)
  if (directKey != null && sizes?.[directKey]) {
    return sizes[directKey]
  }

  const normalizedKey = normalizeSubstrateKey(substrate)
  if (normalizedKey && sizes?.[normalizedKey]) {
    return sizes[normalizedKey]
  }
  return null
}

export function getCustomSizesAllowed({ item, profile }) {
  return Boolean(profile) && !item?.substrate_sizes
}

export function getStandardSizes(substrate, sizes) {
  const result = getSizesForSubstrate(substrate, sizes)
  if (!result) {
    return []
  }

  return result.map((size) => {
    const width = Number(size.width)
    const height = Number(size.height)
    return (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0)
      ? {
        label: `${width} x ${height}`.replace(/\.00/g, ''),
        value: { w: width, h: height },
      }
      : { label: 'Custom', value: { w: 999999.0, h: 999999.0 } }
  })
}

function isAdaptive(userInput) {
  return userInput && (userInput.w === null || userInput.h === null)
}

export function parseDimensionString(input) {
  if (!input || typeof input !== 'string') {
    return { normalized: input, size: { w: null, h: null } }
  }

  const dimensionPattern = /^(.*?)(\d*\.?\d*)\s*([x*×X]|by)?\s*(\d*\.?\d*)(.*?)$/i
  const match = input.match(dimensionPattern)

  if (!match) {
    return { normalized: input, size: { w: null, h: null } }
  }

  const [, prefix, widthStr, separatorStr, heightStr, suffix] = match
  const w = widthStr ? round(parseFloat(widthStr), 2) : null
  const separator = separatorStr ? ' x ' : ''
  const h = heightStr ? round(parseFloat(heightStr), 2) : null
  const normalized = `${prefix}${w ?? ''}${separator}${h ?? ''}${suffix}`

  return {
    normalized,
    size: { w, h },
  }
}

export function createSizeOption(userInput, aspectRatio) {
  const w = userInput.w ?? round(userInput.h * aspectRatio, 1)
  const h = userInput.h ?? round(userInput.w / aspectRatio, 1)
  const suffix = isAdaptive(userInput) ? ' (Auto)' : ' (Custom)'

  return {
    label: `${w} x ${h}${suffix}`,
    value: { w, h },
    userInput,
  }
}

function isValidDimension(value) {
  return value >= 0.1 && value <= 1000
}

export function generateDynamicSizeOptions(input, item) {
  const aspectRatio = getItemAspectRatio(item)
  if (!input || !aspectRatio) {
    return []
  }

  const { size } = parseDimensionString(input)
  const number = size.w || size.h
  if (!number || number <= 0) {
    return []
  }

  const options = [
    { w: number, h: null },
    { w: null, h: number },
  ]

  return options
    .map((userInput) => createSizeOption(userInput, aspectRatio))
    .filter((option) => Object.values(option.value).every(isValidDimension))
}

function regenerateCustomSize(customSize, item) {
  if (!customSize?.userInput) {
    return customSize
  }

  const aspectRatio = getItemAspectRatio(item)
  if (!aspectRatio || !isAdaptive(customSize.userInput)) {
    return customSize
  }

  return createSizeOption(customSize.userInput, aspectRatio)
}

export function getSizeOptions({
  item,
  substrate,
  sizes,
  customSizes = [],
  profile,
  dynamicSizeOptions = [],
}) {
  const standardSizes = getStandardSizes(substrate, sizes)
  if (!getCustomSizesAllowed({ item, profile })) {
    return standardSizes
  }

  const persistedCustomSizes = (customSizes || [])
    .map((size) => regenerateCustomSize(size, item))
    .filter((size) => size?.value?.w && size?.value?.h)

  const combinedSizes = [...standardSizes, ...persistedCustomSizes, ...(dynamicSizeOptions || [])]
  combinedSizes.sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }))

  return uniqBy(combinedSizes, 'label')
}

export function findMatchingSize(size, sizeOptions) {
  if (!size) {
    return null
  }
  if (size.userInput) {
    return sizeOptions.find((option) => option.userInput === size.userInput) || null
  }
  return sizeOptions.find((option) => (
    option.value.w === size.value?.w && option.value.h === size.value?.h
  )) || null
}

export function normalizeSizingSelection({
  item,
  substrate,
  size,
  substrates,
  sizes,
  customSizes = [],
  profile,
  dynamicSizeOptions = [],
}) {
  const substrateOptions = getSubstrateOptions(substrates)
  const normalizedSubstrate = substrateOptions.find((option) => option.value === substrate?.value)
    || substrateOptions.find((option) => option.value === 'print')
    || substrateOptions[0]
    || null

  const sizeOptions = getSizeOptions({
    item,
    substrate: normalizedSubstrate,
    sizes,
    customSizes,
    profile,
    dynamicSizeOptions,
  })
  const standardSizes = getStandardSizes(normalizedSubstrate, sizes)
  const normalizedSize = findMatchingSize(size, sizeOptions)
    || standardSizes[2]
    || standardSizes[0]
    || sizeOptions[0]
    || null

  return {
    substrate: normalizedSubstrate,
    size: normalizedSize,
    substrateOptions,
    sizeOptions,
    standardSizes,
  }
}

export function getSnapCandidates({
  item,
  substrate,
  sizes,
  customSizes = [],
  profile,
}) {
  return getSizeOptions({
    item,
    substrate,
    sizes,
    customSizes,
    profile,
    dynamicSizeOptions: [],
  }).filter((option) => Number.isFinite(Number(option?.value?.w)) && Number.isFinite(Number(option?.value?.h)))
}

export function getMatchedSnapCandidate({
  width,
  height,
  snapCandidates = [],
}) {
  const resolvedWidth = Number(width)
  const resolvedHeight = Number(height)
  if (!Number.isFinite(resolvedWidth) || !Number.isFinite(resolvedHeight)) {
    return null
  }

  return (snapCandidates || []).find((candidate) => {
    return Number(candidate?.value?.w) === resolvedWidth
      && Number(candidate?.value?.h) === resolvedHeight
  }) || null
}

function roundResizeDimension(value) {
  return Math.max(1, Math.round((Number(value) || 0) * 4) / 4)
}

function floorResizeDimension(value) {
  return Math.max(1, Math.floor((Number(value) || 0) * 4) / 4)
}

function parseMatOptionIndex(name) {
  const match = String(name || '').match(/^mat\s*(\d+)$/i)
  if (!match) return null
  const idx = parseInt(match[1], 10) - 1
  return Number.isInteger(idx) && idx >= 0 ? idx : null
}

function normalizeMatWidth(value = {}) {
  return {
    top: parsePositiveNumber(value?.top) || 0,
    bottom: parsePositiveNumber(value?.bottom) || 0,
    left: parsePositiveNumber(value?.left) || 0,
    right: parsePositiveNumber(value?.right) || 0,
  }
}

function getAccumulatedMatInset(matWidthsArray = [], matIndex = null) {
  if (!Number.isInteger(matIndex) || !Array.isArray(matWidthsArray)) {
    return { width: 0, height: 0 }
  }

  // Slots can be sparse (e.g. only Mat 3 selected). Every sheet still spans
  // the full border, regardless of its slot number.
  const widths = matWidthsArray.reduce((acc, value) => {
    const width = normalizeMatWidth(value)
    return {
      top: acc.top + width.top,
      bottom: acc.bottom + width.bottom,
      left: acc.left + width.left,
      right: acc.right + width.right,
    }
  }, { top: 0, bottom: 0, left: 0, right: 0 })

  return {
    width: widths.left + widths.right,
    height: widths.top + widths.bottom,
  }
}

function getTotalMatInset(matWidthsArray = []) {
  if (!Array.isArray(matWidthsArray)) return { width: 0, height: 0 }
  return getAccumulatedMatInset(matWidthsArray, 0)
}

function buildMaxOrientations(maxWidth, maxHeight, inset = { width: 0, height: 0 }) {
  const orientations = [
    {
      width: maxWidth - (Number(inset.width) || 0),
      height: maxHeight - (Number(inset.height) || 0),
    },
  ]

  if (maxWidth !== maxHeight) {
    orientations.push({
      width: maxHeight - (Number(inset.width) || 0),
      height: maxWidth - (Number(inset.height) || 0),
    })
  }

  return uniqBy(orientations
    .filter(orientation => orientation.width > 0 && orientation.height > 0)
    .map(orientation => ({
      width: roundResizeDimension(orientation.width),
      height: roundResizeDimension(orientation.height),
    })), orientation => `${orientation.width}x${orientation.height}`)
}

function getDistance(left, right) {
  return Math.hypot((left.w || 0) - (right.w || 0), (left.h || 0) - (right.h || 0))
}

// Dominant axis quantization: quantize both, pick the largest, derive the other from aspect
function resolveAspectDimensions({ width, height, aspectRatio }) {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    // fallback: quantize both
    return {
      width: roundResizeDimension(width),
      height: roundResizeDimension(height),
    }
  }

  const quantizedWidth = roundResizeDimension(width)
  const quantizedHeight = roundResizeDimension(height)

  // For 1:1 aspect, use the smaller quantized value for both axes
  if (Math.abs(aspectRatio - 1) < 1e-6) {
    const minQ = Math.min(quantizedWidth, quantizedHeight)
    return {
      width: minQ,
      height: minQ,
    }
  }

  // For non-1:1, use the dominant quantized axis and derive the other
  const widthDrivenHeight = quantizedWidth / aspectRatio
  const heightDrivenWidth = quantizedHeight * aspectRatio
  // Pick the quantized axis that results in the smaller area
  if (quantizedWidth < quantizedHeight * aspectRatio) {
    return {
      width: quantizedWidth,
      height: roundResizeDimension(widthDrivenHeight),
    }
  } else {
    return {
      width: roundResizeDimension(heightDrivenWidth),
      height: quantizedHeight,
    }
  }
}

export function resolveResizeDimensions({
  width,
  height,
  mode = RESIZE_MODE_SNAP,
  aspectRatio,
  snapCandidates = [],
}) {
  const roundedWidth = roundResizeDimension(width)
  const roundedHeight = roundResizeDimension(height)

  if (mode === RESIZE_MODE_FREE) {
    return { width: roundedWidth, height: roundedHeight }
  }

  if (mode === RESIZE_MODE_ASPECT || mode === RESIZE_MODE_ASPECT_FREE) {
    // Use dominant axis quantization for aspect-locked resizing
    return resolveAspectDimensions({ width, height, aspectRatio })
  }

  const normalizedCandidates = (snapCandidates || [])
    .map((candidate) => ({
      width: roundResizeDimension(candidate?.value?.w),
      height: roundResizeDimension(candidate?.value?.h),
    }))
    .filter((candidate) => Number.isFinite(candidate.width) && Number.isFinite(candidate.height))

  if (!normalizedCandidates.length) {
    return { width: roundedWidth, height: roundedHeight }
  }

  const raw = { w: width, h: height }
  return normalizedCandidates.reduce((best, candidate) => {
    if (!best) {
      return candidate
    }
    const candidateDistance = getDistance(raw, { w: candidate.width, h: candidate.height })
    const bestDistance = getDistance(raw, { w: best.width, h: best.height })
    return candidateDistance < bestDistance ? candidate : best
  }, null)
}

export function resolveResizeConstraintsFromChosenOptions(chosenOptions = [], { matWidthsArray = [] } = {}) {
  const options = Array.isArray(chosenOptions) ? chosenOptions : []
  const limits = []

  options.forEach((option) => {
    const data = option?.extra_data
    if (!data || typeof data !== 'object') return

    const isFrame = /^frame$/i.test(String(option?.name || '').trim())
    const frameMaxUi = isFrame ? parsePositiveNumber(data.max_ui) : null
    if (frameMaxUi) {
      const maxSize = Math.min(96, frameMaxUi)
      const matInset = getTotalMatInset(matWidthsArray)
      const frameWidth = parsePositiveNumber(data.width) || 0
      const inset = {
        width: matInset.width + (frameWidth * 2),
        height: matInset.height + (frameWidth * 2),
      }
      const maxOrientations = buildMaxOrientations(maxSize, maxSize, inset)
      const primary = maxOrientations[0]
      if (primary) {
        const baseLabel = String(data.constraint_label || option.value || option.name || 'Selected material').trim()
        limits.push({
          type: 'max',
          source: 'frame',
          severity: 'error',
          variant: 'danger',
          clamp: true,
          maxWidth: primary.width,
          maxHeight: primary.height,
          width: primary.width,
          height: primary.height,
          materialMaxWidth: maxSize,
          materialMaxHeight: maxSize,
          insetWidth: inset.width,
          insetHeight: inset.height,
          orientationIndependent: false,
          maxOrientations,
          label: `${baseLabel} frame`,
          message: String(data.max_size_message || data.constraint_message || '').trim(),
        })
      }
    }

    const maxWidth = parsePositiveNumber(data.max_width)
    const maxHeight = parsePositiveNumber(data.max_height)
    if (!maxWidth || !maxHeight) return

    const matIndex = parseMatOptionIndex(option.name)
    const inset = getAccumulatedMatInset(matWidthsArray, matIndex)
    const maxOrientations = buildMaxOrientations(maxWidth, maxHeight, inset)
    if (!maxOrientations.length) return

    const baseLabel = String(data.constraint_label || option.value || option.name || 'Selected material').trim()
    const label = Number.isInteger(matIndex) ? `${baseLabel} mat` : baseLabel
    const message = String(data.max_size_message || data.constraint_message || '').trim()
    const primary = maxOrientations[0]
    limits.push({
      type: 'max',
      source: 'mat',
      severity: 'warning',
      variant: 'warning',
      clamp: false,
      maxWidth: primary.width,
      maxHeight: primary.height,
      width: primary.width,
      height: primary.height,
      materialMaxWidth: maxWidth,
      materialMaxHeight: maxHeight,
      insetWidth: inset.width,
      insetHeight: inset.height,
      orientationIndependent: true,
      maxOrientations,
      label,
      message,
      optionName: option.name,
    })
  })

  return limits
}

export function constraintFitsDimensions(width, height, constraint) {
  const parsedWidth = Number(width)
  const parsedHeight = Number(height)
  if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) return true

  const orientations = Array.isArray(constraint?.maxOrientations) && constraint.maxOrientations.length
    ? constraint.maxOrientations
    : [{ width: constraint?.maxWidth ?? constraint?.width, height: constraint?.maxHeight ?? constraint?.height }]
  return orientations.some(orientation =>
    parsedWidth <= Number(orientation.width ?? orientation.maxWidth) + 0.001 &&
    parsedHeight <= Number(orientation.height ?? orientation.maxHeight) + 0.001
  )
}

/** Mat option names (e.g. "Mat 1") that do not fit the given artwork size for this config. */
export function getMatOptionNamesExceedingSize(width, height, chosenOptions = [], matWidthsArray = []) {
  const exceeded = []
  resolveResizeConstraintsFromChosenOptions(chosenOptions, { matWidthsArray })
    .filter(constraint => constraint.source === 'mat')
    .forEach((constraint) => {
      if (!constraintFitsDimensions(width, height, constraint) && constraint.optionName) {
        exceeded.push(constraint.optionName)
      }
    })
  return exceeded
}

/** Whether artwork dimensions fit all mat-sheet max size constraints for the given config. */
export function artworkFitsMatWidthConstraints(width, height, chosenOptions = [], matWidthsArray = []) {
  const parsedWidth = Number(width)
  const parsedHeight = Number(height)
  if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) return true

  const constraints = resolveResizeConstraintsFromChosenOptions(chosenOptions, { matWidthsArray })
    .filter(constraint => constraint.source === 'mat')
  return constraints.every(constraint => {
    const orientations = Array.isArray(constraint.maxOrientations) && constraint.maxOrientations.length
      ? constraint.maxOrientations
      : [{ width: constraint.maxWidth ?? constraint.width, height: constraint.maxHeight ?? constraint.height }]
    return orientations.some(orientation =>
      parsedWidth <= Number(orientation.width ?? orientation.maxWidth) + 0.001 &&
      parsedHeight <= Number(orientation.height ?? orientation.maxHeight) + 0.001
    )
  })
}

export function clampResizeDimensionsToConstraints({
  width,
  height,
  constraints = [],
  preserveAspectRatio = false,
} = {}) {
  const parsedWidth = Number(width)
  const parsedHeight = Number(height)
  if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) {
    return { width, height, clamped: false, constraint: null }
  }

  const maxConstraints = (constraints || []).filter(constraint => constraint?.clamp !== false && constraint?.type === 'max')
  if (!maxConstraints.length) {
    return { width: parsedWidth, height: parsedHeight, clamped: false, constraint: null }
  }

  const clampToOrientation = (sourceWidth, sourceHeight, orientation) => {
    const maxWidth = parsePositiveNumber(orientation?.width ?? orientation?.maxWidth)
    const maxHeight = parsePositiveNumber(orientation?.height ?? orientation?.maxHeight)
    if (!maxWidth || !maxHeight) return null

    if (preserveAspectRatio && sourceWidth > 0 && sourceHeight > 0) {
      const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight)
      return {
        width: floorResizeDimension(sourceWidth * scale),
        height: floorResizeDimension(sourceHeight * scale),
      }
    }

    return {
      width: Math.min(sourceWidth, maxWidth),
      height: Math.min(sourceHeight, maxHeight),
    }
  }

  const chooseBestClamp = (sourceWidth, sourceHeight, constraint) => {
    const orientations = Array.isArray(constraint.maxOrientations) && constraint.maxOrientations.length
      ? constraint.maxOrientations
      : [{ width: constraint.maxWidth ?? constraint.width, height: constraint.maxHeight ?? constraint.height }]

    const valid = orientations.some((orientation) => {
      const maxWidth = parsePositiveNumber(orientation?.width ?? orientation?.maxWidth)
      const maxHeight = parsePositiveNumber(orientation?.height ?? orientation?.maxHeight)
      return maxWidth && maxHeight && sourceWidth <= maxWidth && sourceHeight <= maxHeight
    })
    if (valid) return { width: sourceWidth, height: sourceHeight, clamped: false }

    return orientations
      .map(orientation => clampToOrientation(sourceWidth, sourceHeight, orientation))
      .filter(Boolean)
      .reduce((best, candidate) => {
        if (!best) return candidate
        const candidateArea = candidate.width * candidate.height
        const bestArea = best.width * best.height
        if (candidateArea !== bestArea) return candidateArea > bestArea ? candidate : best
        const candidateDistance = Math.hypot(sourceWidth - candidate.width, sourceHeight - candidate.height)
        const bestDistance = Math.hypot(sourceWidth - best.width, sourceHeight - best.height)
        return candidateDistance < bestDistance ? candidate : best
      }, null)
  }

  let nextWidth = parsedWidth
  let nextHeight = parsedHeight
  let activeConstraint = null
  let didClamp = false

  maxConstraints.forEach((constraint) => {
    const clamped = chooseBestClamp(nextWidth, nextHeight, constraint)
    if (!clamped) return
    if (clamped.clamped === false) return
    if (clamped.width !== nextWidth || clamped.height !== nextHeight) {
      nextWidth = clamped.width
      nextHeight = clamped.height
      activeConstraint = constraint
      didClamp = true
    }
  })

  if (!didClamp) {
    return { width: parsedWidth, height: parsedHeight, clamped: false, constraint: null }
  }

  return {
    width: nextWidth,
    height: nextHeight,
    clamped: true,
    constraint: activeConstraint,
  }
}
